import React, {
  useState,
} from 'react';

import {
  useTranslation,
} from 'react-i18next';

import {
  useNavigate,
} from 'react-router-dom';

import LanguageSelector
  from '../../../shared/ui/LanguageSelector';

import {
  ROUTES,
} from '../../../config/routes';

import FindAccountForm
  from './FindAccountForm';

import {
  requestFindAccount,
  requestPasswordReset,
} from '../services/findAccountService';


export default function FindAccountSection() {
  const {
    t,
    i18n,
  } =
    useTranslation(
      'login',
    );

  const navigate =
    useNavigate();


  const [
    email,
    setEmail,
  ] =
    useState('');

  const [
    error,
    setError,
  ] =
    useState('');

  const [
    isSubmitting,
    setIsSubmitting,
  ] =
    useState(false);

  const [
    isCompleted,
    setIsCompleted,
  ] =
    useState(false);

  const [
    showNotFoundModal,
    setShowNotFoundModal,
  ] =
    useState(false);


  const [
    resetLoginId,
    setResetLoginId,
  ] =
    useState('');

  const [
    resetEmail,
    setResetEmail,
  ] =
    useState('');

  const [
    resetError,
    setResetError,
  ] =
    useState('');

  const [
    isResetSubmitting,
    setIsResetSubmitting,
  ] =
    useState(false);

  const [
    isResetCompleted,
    setIsResetCompleted,
  ] =
    useState(false);


  const handleEmailChange =
    (
      event,
    ) => {
      setEmail(
        event.target.value,
      );

      setError('');

      setShowNotFoundModal(false);

      setIsCompleted(
        false,
      );
    };


  const handleSubmit =
    async (
      event,
    ) => {
      event.preventDefault();

      const normalizedEmail =
        email
          .trim()
          .toLowerCase();


      if (
        !normalizedEmail
      ) {
        setError(
          t(
            'findAccount.validation.required',
          ),
        );

        return;
      }


      const emailPattern =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


      if (
        !emailPattern.test(
          normalizedEmail,
        )
      ) {
        setError(
          t(
            'findAccount.validation.invalid',
          ),
        );

        return;
      }


      try {
        setIsSubmitting(
          true,
        );

        setError('');

        const result =
          await requestFindAccount(
            normalizedEmail,
            i18n.resolvedLanguage || i18n.language,
          );

        if (!result.matched) {
          setShowNotFoundModal(true);
          return;
        }

        setIsCompleted(true);
      } catch (
      requestError
      ) {
        console.error(
          '[FindAccountSection] Find account failed:',
          requestError,
        );

        setError(
          t(
            'findAccount.error',
          ),
        );
      } finally {
        setIsSubmitting(
          false,
        );
      }
    };


  const handleResetLoginIdChange =
    (
      event,
    ) => {
      setResetLoginId(
        event.target.value,
      );

      setResetError('');

      setIsResetCompleted(
        false,
      );
    };


  const handleResetEmailChange =
    (
      event,
    ) => {
      setResetEmail(
        event.target.value,
      );

      setResetError('');

      setIsResetCompleted(
        false,
      );
    };


  const handlePasswordResetSubmit =
    async (
      event,
    ) => {
      event.preventDefault();

      const normalizedLoginId =
        resetLoginId.trim();

      const normalizedEmail =
        resetEmail
          .trim()
          .toLowerCase();


      if (
        !normalizedLoginId
      ) {
        setResetError(
          t(
            'passwordReset.validation.userIdRequired',
          ),
        );

        return;
      }


      if (
        !normalizedEmail
      ) {
        setResetError(
          t(
            'passwordReset.validation.emailRequired',
          ),
        );

        return;
      }


      const emailPattern =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


      if (
        !emailPattern.test(
          normalizedEmail,
        )
      ) {
        setResetError(
          t(
            'passwordReset.validation.emailInvalid',
          ),
        );

        return;
      }


      try {
        setIsResetSubmitting(
          true,
        );

        setResetError('');

        const result =
          await requestPasswordReset({
            loginId: normalizedLoginId,
            email: normalizedEmail,
            locale: i18n.resolvedLanguage || i18n.language,
          });

        if (!result.matched || !result.sent) {
          setResetError(
            !result.matched
              ? t('passwordReset.notFound')
              : t('passwordReset.error'),
          );
          return;
        }

        setIsResetCompleted(true);
      } catch (
        requestError
      ) {
        console.error(
          '[FindAccountSection] Password reset failed:',
          requestError,
        );

        setResetError(
          t(
            'passwordReset.error',
          ),
        );
      } finally {
        setIsResetSubmitting(
          false,
        );
      }
    };


  const handleBackToLogin =
    () => {
      navigate(
        ROUTES.root,
      );
    };


  const handleContactSales =
    () => {
      navigate(
        ROUTES.contact,
        {
          state: {
            inquiryType:
              'account',
          },
        },
      );
    };


  return (
    <section
      className="
        login-section
        find-account-section
      "
    >
      <div
        className="language-selector-wrapper"
      >
        <LanguageSelector />
      </div>


      {showNotFoundModal && (
        <div
          className="find-account-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowNotFoundModal(false);
            }
          }}
        >
          <div
            className="find-account-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="find-account-not-found-title"
          >
            <h2 id="find-account-not-found-title">
              {t('findAccount.title')}
            </h2>
            <p>{t('findAccount.notFound')}</p>
            <button
              type="button"
              className="login-button find-account-modal-button"
              onClick={() => setShowNotFoundModal(false)}
              autoFocus
            >
              {t('findAccount.modalClose')}
            </button>
          </div>
        </div>
      )}


      <div
        className="
          login-container
          find-account-container
        "
      >
        <FindAccountForm
          email={
            email
          }

          error={
            error
          }

          isSubmitting={
            isSubmitting
          }

          isCompleted={
            isCompleted
          }

          resetLoginId={
            resetLoginId
          }

          resetEmail={
            resetEmail
          }

          resetError={
            resetError
          }

          isResetSubmitting={
            isResetSubmitting
          }

          isResetCompleted={
            isResetCompleted
          }

          onEmailChange={
            handleEmailChange
          }

          onSubmit={
            handleSubmit
          }

          onResetLoginIdChange={
            handleResetLoginIdChange
          }

          onResetEmailChange={
            handleResetEmailChange
          }

          onPasswordResetSubmit={
            handlePasswordResetSubmit
          }

          onBack={
            handleBackToLogin
          }

          onContactSales={
            handleContactSales
          }
        />
      </div>
    </section>
  );
}
